import React, { useState } from "react";
import { api } from "@/lib/api";
import { setCustomerSession, getCustomerSession, clearCustomerSession } from "@/lib/showroomSession";
import { Sparkles, X, Loader2, UserCircle2 } from "lucide-react";
import { errorDetail } from "@/lib/utils";

/**
 * Modal shown on the showroom tablet whenever no customer session is active.
 * Staff enters customer name + 10-digit mobile → backend creates/resumes session.
 */
export default function CustomerOnboarding({ open, onClose, onStarted }) {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const start = async (e) => {
    e?.preventDefault();
    if (!name.trim() || mobile.replace(/\D/g, "").length < 10) {
      alert("Please enter the customer's name and a valid 10-digit mobile number.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/showroom/session/start", { name, mobile });
      setCustomerSession(data);
      onStarted?.(data);
      onClose?.();
      setName("");
      setMobile("");
    } catch (err) {
      alert(errorDetail(err) || "Couldn't start session. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="customer-onboarding-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md overflow-hidden rounded-2xl border border-heda-line bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-heda-line px-5 py-3">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-heda-ink">
            <Sparkles size={16} className="text-heda-gold" /> Welcome to Heda
          </h3>
          <button
            onClick={onClose}
            className="rounded-full p-1.5 text-heda-mute hover:bg-heda-cream/60"
            data-testid="customer-onboarding-close"
          >
            <X size={16} />
          </button>
        </div>
        <form onSubmit={start} className="space-y-4 p-5">
          <div>
            <label className="heda-label" htmlFor="customer-name">Customer name</label>
            <input
              id="customer-name"
              data-testid="customer-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Mr. Arjun Mehta"
              autoFocus
              className="heda-input"
            />
          </div>
          <div>
            <label className="heda-label" htmlFor="customer-mobile">Mobile number</label>
            <input
              id="customer-mobile"
              data-testid="customer-mobile-input"
              inputMode="numeric"
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/[^\d+\-\s]/g, ""))}
              placeholder="98xxxxxxxx"
              className="heda-input"
            />
            <p className="mt-1.5 text-[11px] text-heda-mute">
              If this customer has visited before, their wishlist and history will load automatically.
            </p>
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="customer-onboarding-start"
            className="heda-btn w-full"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <UserCircle2 size={14} />}
            Start showroom session
          </button>
        </form>
      </div>
    </div>
  );
}

/**
 * Small staff control rendered in the admin top-bar showing active customer session,
 * with buttons to start a new session or end the current one.
 */
export function CustomerSessionPill() {
  const [session, setSession] = useState(() => getCustomerSession());
  const [open, setOpen] = useState(false);

  // Listen for session changes from anywhere in the app.
  React.useEffect(() => {
    const handler = () => setSession(getCustomerSession());
    window.addEventListener("heda-customer-session-changed", handler);
    return () => window.removeEventListener("heda-customer-session-changed", handler);
  }, []);

  const end = async () => {
    if (!window.confirm("End this customer session?")) return;
    try {
      await api.post("/showroom/session/end");
    } catch {
      /* still clear locally */
    }
    clearCustomerSession();
    setSession(null);
  };

  return (
    <>
      {session ? (
        <div
          className="inline-flex items-center gap-2 rounded-full border border-heda-gold/40 bg-heda-gold-tint py-1 pl-3 pr-1 text-[12.5px]"
          data-testid="customer-session-pill"
        >
          <UserCircle2 size={13} className="text-heda-gold" />
          <span className="font-semibold text-heda-ink">{session.name}</span>
          <span className="text-heda-sub">·{session.mobile.slice(-4)}</span>
          {session.is_returning && (
            <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-heda-gold">
              Visit #{session.visit_count}
            </span>
          )}
          <button
            onClick={end}
            className="rounded-full p-1.5 text-heda-mute hover:bg-white hover:text-red-700"
            title="End session"
            data-testid="customer-session-end"
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          data-testid="customer-session-start-button"
          className="heda-pill"
        >
          <UserCircle2 size={13} />
          Start customer session
        </button>
      )}
      <CustomerOnboarding open={open} onClose={() => setOpen(false)} onStarted={setSession} />
    </>
  );
}
